import { Timeline, Tag, Spin, Empty, Typography } from 'antd';
import {
  ShopOutlined,
  RocketOutlined,
  StopOutlined,
  WarningOutlined,
  UserOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  SafetyOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { fetchOrderEvents, type OrderEvent, type ActorType } from '../api/orderEvents.api';

const { Text } = Typography;

const ACTOR_COLORS: Record<ActorType, string> = {
  system: 'default',
  shipstation: 'blue',
  uniuni: 'purple',
  user: 'green',
  batch_guard: 'red',
};

const ACTOR_LABELS: Record<ActorType, string> = {
  system: 'System',
  shipstation: 'ShipStation',
  uniuni: 'UniUni',
  user: 'User',
  batch_guard: 'Batch Guard',
};

function iconFor(evt: OrderEvent) {
  const type = evt.event_type;
  if (type.startsWith('shipstation.imported')) return <ShopOutlined style={{ fontSize: 16 }} />;
  if (type.startsWith('shipstation.status')) return <ClockCircleOutlined style={{ fontSize: 16 }} />;
  if (type.startsWith('shipstation.warehouse')) return <DatabaseOutlined style={{ fontSize: 16 }} />;
  if (type.startsWith('shipstation.carrier')) return <ThunderboltOutlined style={{ fontSize: 16 }} />;
  if (type === 'uniuni.shipment_created') return <RocketOutlined style={{ fontSize: 16 }} />;
  if (type === 'uniuni.shipment_voided') return <StopOutlined style={{ fontSize: 16 }} />;
  if (type === 'uniuni.shipment_failed') return <WarningOutlined style={{ fontSize: 16 }} />;
  if (type === 'batch.rejected') return <SafetyOutlined style={{ fontSize: 16 }} />;
  if (type === 'order.item_added') return <ShopOutlined style={{ fontSize: 16 }} />;
  if (type === 'order.item_removed') return <StopOutlined style={{ fontSize: 16 }} />;
  if (type === 'order.item_quantity_changed') return <ClockCircleOutlined style={{ fontSize: 16 }} />;
  if (type.startsWith('inventory')) return <DatabaseOutlined style={{ fontSize: 16 }} />;
  if (evt.actor_type === 'user') return <UserOutlined style={{ fontSize: 16 }} />;
  return <ClockCircleOutlined style={{ fontSize: 16 }} />;
}

function colorFor(evt: OrderEvent): string {
  const type = evt.event_type;
  if (type === 'uniuni.shipment_created') return 'green';
  if (type === 'uniuni.shipment_voided') return 'red';
  if (type === 'uniuni.shipment_failed') return 'red';
  if (type === 'batch.rejected') return 'red';
  if (type === 'shipstation.imported') return 'blue';
  if (type.startsWith('shipstation')) return 'blue';
  if (type === 'inventory.decremented') return 'orange';
  if (type === 'inventory.reversed') return 'green';
  if (type === 'order.item_added') return 'blue';
  if (type === 'order.item_removed') return 'red';
  if (type === 'order.item_quantity_changed') return 'orange';
  return 'gray';
}

function relativeTime(iso: string): string {
  const now = dayjs();
  const then = dayjs(iso);
  const diffMin = now.diff(then, 'minute');
  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = now.diff(then, 'hour');
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = now.diff(then, 'day');
  if (diffDay < 30) return `${diffDay}d ago`;
  return then.format('MMM D, YYYY');
}

export default function OrderTimeline({ orderId }: { orderId: string | number }) {
  const { data: events = [], isLoading } = useQuery<OrderEvent[]>({
    queryKey: ['order-events', String(orderId)],
    queryFn: () => fetchOrderEvents(String(orderId)),
    enabled: Boolean(orderId),
    staleTime: 30_000,
  });

  if (isLoading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        <Spin size="small" />
      </div>
    );
  }

  if (events.length === 0) {
    return <Empty description="No activity recorded yet" />;
  }

  return (
    <Timeline
      mode="left"
      items={events.map((evt) => ({
        color: colorFor(evt),
        dot: iconFor(evt),
        children: (
          <div style={{ marginBottom: 4 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <Text style={{ fontSize: 12, color: '#8c8c8c' }}>
                {dayjs(evt.created_at).format('MM/DD/YYYY HH:mm')}
              </Text>
              <Text style={{ fontSize: 12, color: '#bfbfbf' }}>({relativeTime(evt.created_at)})</Text>
              <Text style={{ fontSize: 12, color: '#8c8c8c' }}>by:</Text>
              <Tag color={ACTOR_COLORS[evt.actor_type]} style={{ margin: 0 }}>
                {evt.actor_name || ACTOR_LABELS[evt.actor_type]}
              </Tag>
            </div>
            <div style={{ marginTop: 4, fontSize: 14, color: '#262626' }}>
              {evt.message ?? evt.event_type}
            </div>
            {evt.metadata && Object.keys(evt.metadata).length > 0 && (
              <div style={{ marginTop: 4, fontSize: 12, color: '#8c8c8c' }}>
                {Object.entries(evt.metadata)
                  .filter(([, v]) => v != null && v !== '')
                  .slice(0, 4)
                  .map(([k, v]) => (
                    <span key={k} style={{ marginRight: 12 }}>
                      <b>{k}:</b> {typeof v === 'object' ? JSON.stringify(v) : String(v)}
                    </span>
                  ))}
              </div>
            )}
          </div>
        ),
      }))}
    />
  );
}
